import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Paper,
  Tooltip,
  ButtonGroup
} from '@mui/material';
import {
  Undo,
  Redo,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight
} from '@mui/icons-material';

interface FormattingState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  fontSize: number;
  alignment: 'left' | 'center' | 'right';
}

interface FormattingToolbarProps {
  editorRef: React.RefObject<HTMLDivElement | null>;
  disabled?: boolean;
}

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28];
const THEME_GREEN = '#2F7A4F';

const FormattingToolbar: React.FC<FormattingToolbarProps> = ({ 
  editorRef, 
  disabled = false 
}) => {
  const [formattingState, setFormattingState] = useState<FormattingState>({
    bold: false,
    italic: false,
    underline: false,
    fontSize: 12,
    alignment: 'left'
  });

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update formatting state based on current selection
  const updateFormattingState = useCallback(() => {
    if (!editorRef.current || disabled) return;

    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      // Check if selection is within our editor
      const range = selection.getRangeAt(0);
      const isInEditor = editorRef.current.contains(range.commonAncestorContainer);
      if (!isInEditor) return;

      // Get formatting states using queryCommandState
      const bold = document.queryCommandState('bold');
      const italic = document.queryCommandState('italic');
      const underline = document.queryCommandState('underline');

      // Get font size from current selection
      let fontSize = 12;
      const parentElement = range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement
        : range.startContainer as Element;

      if (parentElement) {
        const computedStyle = window.getComputedStyle(parentElement);
        const fontSizeStr = computedStyle.fontSize;
        if (fontSizeStr) {
          const detectedSize = Math.round(parseFloat(fontSizeStr));
          // Ensure the detected font size is in the allowed FONT_SIZES array
          fontSize = FONT_SIZES.includes(detectedSize) ? detectedSize : 12;
        }
      }

      // Get text alignment - check the current paragraph
      let alignment: 'left' | 'center' | 'right' = 'left';
      
      const currentSelection = window.getSelection();
      if (currentSelection && currentSelection.rangeCount > 0) {
        const range = currentSelection.getRangeAt(0);
        
        // Check if an image is selected first
        const selectedImage = editorRef.current?.querySelector('.inline-image.selected');
        let targetParagraph: HTMLElement | null = null;
        
        if (selectedImage) {
          targetParagraph = selectedImage.closest('p') as HTMLElement;
        } else {
          // Find the paragraph containing the cursor/selection
          let currentNode: Node | null = range.startContainer;
          while (currentNode && currentNode.nodeType !== Node.ELEMENT_NODE) {
            currentNode = currentNode.parentNode;
          }
          targetParagraph = (currentNode as Element)?.closest('p') as HTMLElement;
        }
        
        if (targetParagraph) {
          // Check both inline style and computed style
          const inlineAlign = targetParagraph.style.textAlign;
          const computedStyle = window.getComputedStyle(targetParagraph);
          const computedAlign = computedStyle.textAlign;
          
          // Prioritize inline style, then computed style
          const textAlign = inlineAlign || computedAlign;
          if (textAlign === 'center') alignment = 'center';
          else if (textAlign === 'right') alignment = 'right';
        }
      }

      // Check undo/redo availability
      const undoAvailable = document.queryCommandEnabled('undo');
      const redoAvailable = document.queryCommandEnabled('redo');

      setFormattingState({
        bold,
        italic,
        underline,
        fontSize,
        alignment
      });

      setCanUndo(undoAvailable);
      setCanRedo(redoAvailable);

    } catch (error) {
      console.warn('Error updating formatting state:', error);
    }
  }, [editorRef, disabled]);

  // Debounced update to avoid excessive re-renders
  const debouncedUpdateFormattingState = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(updateFormattingState, 50);
  }, [updateFormattingState]);

  // Listen for selection changes and key events
  useEffect(() => {
    const currentEditor = editorRef.current;
    if (!currentEditor || disabled) return;

    const handleSelectionChange = () => {
      debouncedUpdateFormattingState();
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      // Update on key releases that might change formatting
      if (event.key === 'ArrowLeft' || 
          event.key === 'ArrowRight' || 
          event.key === 'ArrowUp' || 
          event.key === 'ArrowDown' ||
          event.metaKey || 
          event.ctrlKey) {
        debouncedUpdateFormattingState();
      }
    };

    const handleInput = () => {
      debouncedUpdateFormattingState();
    };

    // Add event listeners
    document.addEventListener('selectionchange', handleSelectionChange);
    currentEditor.addEventListener('keyup', handleKeyUp);
    currentEditor.addEventListener('input', handleInput);

    // Initial update
    updateFormattingState();

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (currentEditor) {
        currentEditor.removeEventListener('keyup', handleKeyUp);
        currentEditor.removeEventListener('input', handleInput);
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [editorRef, disabled, debouncedUpdateFormattingState, updateFormattingState]);

  // Execute formatting commands
  const executeCommand = useCallback((command: string, value?: string) => {
    if (!editorRef.current || disabled) return;

    try {
      // Focus the editor first
      editorRef.current.focus();
      
      // Execute the command
      document.execCommand(command, false, value);
      
      // Update formatting state immediately
      setTimeout(updateFormattingState, 10);
      
    } catch (error) {
      console.warn('Error executing formatting command:', error);
    }
  }, [editorRef, disabled, updateFormattingState]);

  // Handle font size change with custom styling
  const handleFontSizeChange = useCallback((size: number) => {
    if (!editorRef.current || disabled) return;

    try {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      
      if (range.collapsed) {
        // No text selected - set up for future typing
        document.execCommand('fontSize', false, '7'); // Use index for browser compatibility
        
        // Replace browser's font tag with span
        setTimeout(() => {
          const fontElements = editorRef.current?.querySelectorAll('font[size="7"]');
          fontElements?.forEach(font => {
            const span = document.createElement('span');
            span.style.fontSize = `${size}px`;
            span.innerHTML = font.innerHTML;
            font.parentNode?.replaceChild(span, font);
          });
        }, 10);
      } else {
        // Text is selected - wrap in span
        const selectedText = range.extractContents();
        const span = document.createElement('span');
        span.style.fontSize = `${size}px`;
        span.appendChild(selectedText);
        range.insertNode(span);
        
        // Update selection to include the new span
        range.selectNode(span);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      updateFormattingState();
    } catch (error) {
      console.warn('Error changing font size:', error);
    }
  }, [editorRef, disabled, updateFormattingState]);

  // Handle alignment changes with smart line detection
  const handleAlignment = useCallback((align: 'left' | 'center' | 'right') => {
    if (!editorRef.current || disabled) return;

    try {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (!selection) return;

      // Handle case where there's no selection range (blank document)
      if (selection.rangeCount === 0) {
        // Check if editor has existing content we can align
        const existingParagraphs = editorRef.current.querySelectorAll('p');
        if (existingParagraphs.length === 1 && existingParagraphs[0].innerHTML === '<br>') {
          // Single empty paragraph - just apply alignment to it
          const paragraph = existingParagraphs[0] as HTMLElement;
          if (align !== 'left') {
            paragraph.style.textAlign = align;
          } else {
            paragraph.style.textAlign = '';
            if (!paragraph.getAttribute('style') || paragraph.getAttribute('style')?.trim() === '') {
              paragraph.removeAttribute('style');
            }
          }
          
          // Position cursor in the paragraph
          const range = document.createRange();
          range.setStart(paragraph, 0);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // Create a new paragraph with the desired alignment
          const newParagraph = document.createElement('p');
          if (align !== 'left') {
            newParagraph.style.textAlign = align;
          }
          newParagraph.innerHTML = '<br>';
          
          // Clear editor content and insert aligned paragraph
          editorRef.current.innerHTML = '';
          editorRef.current.appendChild(newParagraph);
          
          // Position cursor in the new paragraph
          const range = document.createRange();
          range.setStart(newParagraph, 0);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        // Trigger input event to save changes
        editorRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        setTimeout(updateFormattingState, 10);
        return;
      }

      const range = selection.getRangeAt(0);
      
      // Function to get all paragraph elements that should be aligned
      const getParagraphsToAlign = (): HTMLElement[] => {
        const paragraphs: Set<HTMLElement> = new Set();
        
        // Check if an image is selected
        const selectedImage = editorRef.current?.querySelector('.inline-image.selected');
        if (selectedImage) {
          // If image is selected, align the paragraph containing the image
          const imgParagraph = selectedImage.closest('p') as HTMLElement;
          if (imgParagraph) {
            paragraphs.add(imgParagraph);
          }
          return Array.from(paragraphs);
        }
        
        if (range.collapsed) {
          // Cursor position - align the current paragraph
          let currentNode: Node | null = range.startContainer;
          while (currentNode && currentNode.nodeType !== Node.ELEMENT_NODE) {
            currentNode = currentNode.parentNode;
          }
          
          const paragraph = (currentNode as Element)?.closest('p') as HTMLElement;
          if (paragraph) {
            paragraphs.add(paragraph);
          } else {
            // No paragraph found - create one at cursor position
            const newParagraph = document.createElement('p');
            if (align !== 'left') {
              newParagraph.style.textAlign = align;
            }
            newParagraph.innerHTML = '<br>';
            
            // If editor is completely empty, just append the paragraph
            if (editorRef.current && (!editorRef.current.hasChildNodes() || editorRef.current.innerHTML.trim() === '')) {
              editorRef.current.innerHTML = '';
              editorRef.current.appendChild(newParagraph);
            } else {
              // Insert at cursor position
              range.deleteContents();
              range.insertNode(newParagraph);
            }
            
            // Position cursor in new paragraph
            range.setStart(newParagraph, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            
            paragraphs.add(newParagraph);
          }
        } else {
          // Text is selected - find all paragraphs that intersect with the selection
          const walker = document.createTreeWalker(
            range.commonAncestorContainer,
            NodeFilter.SHOW_ELEMENT,
            {
              acceptNode: (node) => {
                if (node.nodeName === 'P' && range.intersectsNode(node)) {
                  return NodeFilter.FILTER_ACCEPT;
                }
                return NodeFilter.FILTER_SKIP;
              }
            }
          );
          
          let node;
          while (node = walker.nextNode()) {
            paragraphs.add(node as HTMLElement);
          }
          
          // Also check start and end containers for their parent paragraphs
          const startParagraph = range.startContainer.nodeType === Node.ELEMENT_NODE 
            ? (range.startContainer as Element).closest('p')
            : range.startContainer.parentElement?.closest('p');
          const endParagraph = range.endContainer.nodeType === Node.ELEMENT_NODE
            ? (range.endContainer as Element).closest('p') 
            : range.endContainer.parentElement?.closest('p');
            
          if (startParagraph) paragraphs.add(startParagraph as HTMLElement);
          if (endParagraph) paragraphs.add(endParagraph as HTMLElement);
        }
        
        return Array.from(paragraphs);
      };

      // Get all paragraphs that need alignment
      const paragraphsToAlign = getParagraphsToAlign();
      
      // Apply alignment to each paragraph
      paragraphsToAlign.forEach(paragraph => {
        if (paragraph && paragraph.nodeType === Node.ELEMENT_NODE) {
          // CRITICAL: Ensure paragraph remains editable
          paragraph.removeAttribute('contenteditable');
          
          // Remove any existing alignment
          paragraph.style.textAlign = '';
          
          // Apply new alignment
          if (align !== 'left') { // 'left' is default, no need to set it explicitly
            paragraph.style.textAlign = align;
          }
          
          // Also update the style attribute to ensure persistence
          const currentStyle = paragraph.getAttribute('style') || '';
          const alignmentRegex = /text-align:\s*[^;]+;?/g;
          let newStyle = currentStyle.replace(alignmentRegex, '');
          
          if (align !== 'left') {
            newStyle = newStyle.trim();
            if (newStyle && !newStyle.endsWith(';')) newStyle += ';';
            newStyle += ` text-align: ${align};`;
          }
          
          if (newStyle.trim()) {
            paragraph.setAttribute('style', newStyle);
          } else {
            paragraph.removeAttribute('style');
          }
          
          // 🎯 NEW: ALIGN IMAGES ON THE SAME LINE
          // Find all images (inline-image-container) within this paragraph
          const imageContainers = paragraph.querySelectorAll('.inline-image-container');
          imageContainers.forEach(container => {
            const imgContainer = container as HTMLElement;
            
            // Remove existing alignment classes
            imgContainer.classList.remove('image-float-left', 'image-float-right', 'image-center', 'image-inline');
            
            // Apply new alignment based on paragraph alignment
            if (align === 'center') {
              imgContainer.classList.add('image-center');
              imgContainer.style.float = 'none';
              imgContainer.style.display = 'block';
              imgContainer.style.margin = '0 auto 10px auto';
            } else if (align === 'right') {
              imgContainer.classList.add('image-float-right');
              imgContainer.style.float = 'right';
              imgContainer.style.display = 'block';
              imgContainer.style.margin = '0 0 10px 10px';
            } else {
              // Default to left alignment
              imgContainer.classList.add('image-float-left');
              imgContainer.style.float = 'left';
              imgContainer.style.display = 'block';
              imgContainer.style.margin = '0 10px 10px 0';
            }
            
            console.log(`✅ Aligned image in paragraph to ${align}`);
          });
        }
      });
      
      // Trigger input event to save changes
      if (paragraphsToAlign.length > 0) {
        editorRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }
      
      // Update formatting state
      setTimeout(updateFormattingState, 10);
      
    } catch (error) {
      console.warn('Error setting alignment:', error);
    }
  }, [editorRef, disabled, updateFormattingState]);

  const buttonStyle = (isActive: boolean) => ({
    backgroundColor: isActive ? THEME_GREEN : 'transparent',
    color: isActive ? 'white' : '#666',
    border: isActive ? `1px solid ${THEME_GREEN}` : '1px solid transparent',
    '&:hover': {
      backgroundColor: isActive ? THEME_GREEN : '#f5f5f5',
      borderColor: isActive ? THEME_GREEN : 'transparent'
    },
    width: 32,
    height: 32,
    minWidth: 32
  });

  return (
    <Paper
      elevation={2}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        padding: '8px 16px',
        margin: '0 0 16px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        maxWidth: '600px',
        boxSizing: 'border-box'
      }}
    >
      {/* Undo/Redo */}
      <ButtonGroup variant="outlined" size="small">
        <Tooltip title="Undo">
          <span>
            <IconButton
              onClick={() => executeCommand('undo')}
              disabled={disabled || !canUndo}
              sx={buttonStyle(false)}
            >
              <Undo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo">
          <span>
            <IconButton
              onClick={() => executeCommand('redo')}
              disabled={disabled || !canRedo}
              sx={buttonStyle(false)}
            >
              <Redo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Text Formatting */}
      <ButtonGroup variant="outlined" size="small">
        <Tooltip title="Bold">
          <span>
            <IconButton
              onClick={() => executeCommand('bold')}
              disabled={disabled}
              sx={buttonStyle(formattingState.bold)}
            >
              <FormatBold fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Italic">
          <span>
            <IconButton
              onClick={() => executeCommand('italic')}
              disabled={disabled}
              sx={buttonStyle(formattingState.italic)}
            >
              <FormatItalic fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Underline">
          <span>
            <IconButton
              onClick={() => executeCommand('underline')}
              disabled={disabled}
              sx={buttonStyle(formattingState.underline)}
            >
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Alignment */}
      <ButtonGroup variant="outlined" size="small">
        <Tooltip title="Align Left">
          <span>
            <IconButton
              onClick={() => handleAlignment('left')}
              disabled={disabled}
              sx={buttonStyle(formattingState.alignment === 'left')}
            >
              <FormatAlignLeft fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Align Center">
          <span>
            <IconButton
              onClick={() => handleAlignment('center')}
              disabled={disabled}
              sx={buttonStyle(formattingState.alignment === 'center')}
            >
              <FormatAlignCenter fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Align Right">
          <span>
            <IconButton
              onClick={() => handleAlignment('right')}
              disabled={disabled}
              sx={buttonStyle(formattingState.alignment === 'right')}
            >
              <FormatAlignRight fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Font Size */}
      <FormControl size="small" sx={{ minWidth: 70 }}>
        <Select
          value={formattingState.fontSize}
          onChange={(e) => handleFontSizeChange(e.target.value as number)}
          disabled={disabled}
          sx={{
            height: 32,
            backgroundColor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ddd'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ccc'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: THEME_GREEN
            }
          }}
        >
          {FONT_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Paper>
  );
};

export default FormattingToolbar;
